import { Sparkle, ArrowSquareOut } from "@phosphor-icons/react";
import classNames from "classnames";
import * as React from "react";
import styles from "./Navbar.module.css";
import { Button } from "../Button";
import { MobileResourcesContent } from "./mobile-content/MobileResourcesContent";
import { MobileSolutionsContent } from "./mobile-content/MobileSolutionsContent";
import { MobileNavItem } from "./MobileNavItem";
import { SafeAuthButtons } from "./SafeAuthButtons";
import { AuthenticationContext } from "~/contexts/authenticationContext";
import { ReactRouterStubsContext } from "~/contexts/reactRouterStubsContext";
import * as Analytics from "~/hooks/useAnalytics";
import { useLogout } from "~/hooks/useLogout";
import * as paths from "~/paths";

export interface MobileMenuProps {
  isOpen: boolean;
  animationState: "closed" | "opening" | "open" | "closing";
  expandedItem: string | null;
  onToggleItem: (itemName: string) => void;
  nimbusUrl?: string;
  chromeExtensionUrl?: string;
}

export const MobileMenu: React.FC<MobileMenuProps> = ({
  isOpen: _isOpen,
  animationState,
  expandedItem,
  onToggleItem,
  nimbusUrl,
  chromeExtensionUrl,
}) => {
  const { identity } = React.useContext(AuthenticationContext);
  const stubs = React.useContext(ReactRouterStubsContext);
  const { trackButton } = Analytics.useAnalytics();
  const { isLoggingOut, handleLogoutSubmit } = useLogout();

  if (animationState === "closed") {
    return null;
  }

  const menuItems = [
    { name: "Ruth", hasDropdown: false, href: "/ruth" },
    { name: "Solutions", hasDropdown: true },
    { name: "Resources", hasDropdown: true },
    { name: "Pricing", hasDropdown: false, href: "/pricing" },
  ];

  return (
    <div
      className={classNames(
        styles.mobileMenuWrapper,
        animationState === "open" && styles.mobileMenuOpen,
        animationState === "closing" && styles.mobileMenuClosing,
      )}
    >
      <div className={styles.mobileMenuContent}>
        <div className={styles.mobileNavItems}>
          {menuItems.map((item) => (
            <MobileNavItem
              key={item.name}
              name={item.name}
              hasDropdown={item.hasDropdown}
              href={item.href}
              isExpanded={expandedItem === item.name}
              onToggle={() => item.hasDropdown && onToggleItem(item.name)}
            >
              {item.name === "Solutions" && <MobileSolutionsContent />}
              {item.name === "Resources" && (
                <MobileResourcesContent
                  chromeExtensionUrl={chromeExtensionUrl}
                />
              )}
            </MobileNavItem>
          ))}
        </div>

        <div className={styles.mobileMenuActions}>
          {identity ? (
            <>
              <div className={styles.mobileUserInfo}>
                <div className={styles.mobileUserAvatar}>
                  {identity.name?.charAt(0).toUpperCase() ||
                    identity.userEmail?.charAt(0).toUpperCase() ||
                    "U"}
                </div>
                <div className={styles.mobileUserDetails}>
                  {identity.name && (
                    <span className={styles.mobileUserName}>
                      {identity.name}
                    </span>
                  )}
                  {identity.userEmail && (
                    <span className={styles.mobileUserEmail}>
                      {identity.userEmail}
                    </span>
                  )}
                </div>
              </div>
              {nimbusUrl && (
                <stubs.Link
                  to={nimbusUrl}
                  className={styles.mobileRuthLink}
                  onClick={() => {
                    trackButton({
                      element_text: "Chat with Ruth",
                      element_location: "Site mobile navigation",
                    });
                  }}
                >
                  <Sparkle size={18} weight="regular" />
                  <span>Chat with Ruth</span>
                  <ArrowSquareOut size={12} weight="bold" />
                </stubs.Link>
              )}
              <div className={styles.mobileAuthButtons}>
                <stubs.Form
                  method="post"
                  action={paths.logout({ redirectBackTo: stubs.location })}
                  onSubmit={handleLogoutSubmit}
                  className={styles.mobileLogoutForm}
                >
                  <Button
                    type="submit"
                    variant="secondary"
                    size="small"
                    className={styles.mobileCTAButton}
                    disabled={isLoggingOut}
                  >
                    {isLoggingOut ? "Logging out..." : "Log out"}
                  </Button>
                </stubs.Form>
              </div>
            </>
          ) : (
            <SafeAuthButtons variant="mobile" />
          )}
        </div>
      </div>
    </div>
  );
};
